import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { ENV_STORAGE_KEY } from '../config';
import type { PortalEnvironment } from '../api/types';

interface EnvironmentContextValue {
  environment: PortalEnvironment;
  setEnvironment: (env: PortalEnvironment) => void;
  isSandbox: boolean;
  accentClass: string;
  accentRing: string;
  accentGradient: string;
}

const EnvironmentContext = createContext<EnvironmentContextValue | null>(null);

function loadEnv(): PortalEnvironment {
  const stored = localStorage.getItem(ENV_STORAGE_KEY);
  return stored === 'production' ? 'production' : 'sandbox';
}

export function EnvironmentProvider({ children }: { children: ReactNode }) {
  const [environment, setEnvState] = useState<PortalEnvironment>(loadEnv);

  const setEnvironment = useCallback((env: PortalEnvironment) => {
    localStorage.setItem(ENV_STORAGE_KEY, env);
    setEnvState(env);
  }, []);

  const value = useMemo<EnvironmentContextValue>(() => {
    const isSandbox = environment === 'sandbox';
    return {
      environment,
      setEnvironment,
      isSandbox,
      accentClass: isSandbox ? 'text-onyx-purple' : 'text-onyx-gold',
      accentRing: isSandbox ? 'focus:ring-onyx-purple' : 'focus:ring-onyx-gold',
      accentGradient: isSandbox
        ? 'from-onyx-purple/20 via-onyx-purple-dim/10 to-transparent'
        : 'from-onyx-gold/20 via-onyx-gold-dim/10 to-transparent',
    };
  }, [environment, setEnvironment]);

  return <EnvironmentContext.Provider value={value}>{children}</EnvironmentContext.Provider>;
}

export function useEnvironment() {
  const ctx = useContext(EnvironmentContext);
  if (!ctx) throw new Error('useEnvironment must be used within EnvironmentProvider');
  return ctx;
}
